"use client";

import Link from "next/link";
import { useState, useSyncExternalStore } from "react";
import { acknowledgeSessionRevocationWarning, hasSessionRevocationWarning, sessionRevocationWarningText, type SessionRevocationAccountKind } from "../../lib/sessionRevocationWarning";
import StaffStepUpVerification from "./StaffStepUpVerification";

const subscribe = () => () => {};

export default function SessionRevocationReviewBanner({ accountKind, review = false }: { accountKind: SessionRevocationAccountKind; review?: boolean }) {
  const pending = useSyncExternalStore(subscribe, () => hasSessionRevocationWarning(accountKind), () => false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stepUp, setStepUp] = useState(false);
  if (!pending || dismissed) return null;
  const loginHref = accountKind === "staff" ? "/admin/login" : "/login";
  const accountHref = accountKind === "staff" ? "/admin/profile" : "/portal/account#account-security";

  async function retry() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(accountKind === "staff" ? "/api/admin/staff/session-revocation/retry" : "/api/customer/session-revocation/retry", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}", credentials: "same-origin", redirect: "error",
      });
      const result = await response.json();
      if (!response.ok) {
        if (accountKind === "staff" && response.status === 403 && result.stepUpRequired === true) {
          setStepUp(true);
          return;
        }
        throw new Error("Session revocation could not be confirmed. Sign in again to review your account security.");
      }
      if (result.state !== "revoked" && result.state !== "still_pending") throw new Error("Session revocation could not be confirmed.");
      if (result.state === "revoked") acknowledgeSessionRevocationWarning(accountKind);
      // Both accepted and unconfirmed attempts terminate the invoking session.
      window.location.assign(loginHref);
    } catch {
      setError("Session revocation could not be confirmed. Sign in again to review your account security.");
    } finally { setBusy(false); }
  }

  return <section role="alert" className="my-5 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
    <h2 className="font-bold">Review other sessions</h2>
    <p className="mt-2 leading-6">{sessionRevocationWarningText}</p>
    {review ? <>
      <p className="mt-2 leading-6">This action applies to your currently signed-in account and signs you out. Existing access tokens may remain valid until expiry.</p>
      <div className="mt-3 flex flex-wrap gap-3">
        <button type="button" disabled={busy} onClick={retry} className="min-h-11 rounded-md bg-[var(--vintera-navy)] px-4 py-2 font-semibold text-white disabled:opacity-60">{busy ? "Revoking…" : "Revoke other sessions"}</button>
        <button type="button" disabled={busy} onClick={() => { acknowledgeSessionRevocationWarning(accountKind); setDismissed(true); }} className="min-h-11 rounded-md border border-amber-400 px-4 py-2 font-semibold">Acknowledge warning</button>
      </div>
      {error && <p role="alert" className="mt-3">{error} <Link href={loginHref} className="font-semibold underline">Sign in again</Link></p>}
      {stepUp && <StaffStepUpVerification onCancel={() => setStepUp(false)} onVerified={() => { setStepUp(false); void retry(); }} />}
    </> : <p className="mt-2">After signing in and completing verification, <Link href={accountHref} className="font-semibold underline">review Account security</Link>.</p>}
  </section>;
}
